(() => {
  const initialized = new WeakMap();
  const getRoot = (scope) => scope?.matches?.('.icz-halloween') ? scope : scope?.querySelector?.('.icz-halloween');

  const init = (scope = document) => {
    const root = getRoot(scope);
    if (!root || initialized.has(root)) return;
    initialized.set(root, null);

    const cleanups = [];
    const on = (target, event, handler, options) => {
      target.addEventListener(event, handler, options);
      cleanups.push(() => target.removeEventListener(event, handler, options));
    };
    const qs = (selector) => root.querySelector(selector);
    const qsa = (selector) => [...root.querySelectorAll(selector)];

    const header = qs('[data-header]');
    const hero = qs('.icz-hero');
    if (header && hero && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        header.classList.toggle('is-solid', !entry.isIntersecting);
      }, { threshold: 0.02 });
      observer.observe(hero);
      cleanups.push(() => observer.disconnect());
    }

    const toggle = qs('.icz-menu-toggle');
    const menu = qs('.icz-mobile-nav');
    const closeMenu = () => {
      if (!toggle || !menu) return;
      toggle.setAttribute('aria-expanded', 'false');
      menu.hidden = true;
    };
    if (toggle && menu) {
      on(toggle, 'click', () => {
        const open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!open));
        menu.hidden = open;
      });
      qsa('.icz-mobile-nav a').forEach((link) => on(link, 'click', closeMenu));
      on(document, 'keydown', (event) => {
        if (event.key === 'Escape') closeMenu();
      });
    }

    const cards = qsa('[data-products] .icz-product-card');
    const filters = qsa('[data-filter]');
    const search = qs('[data-search]');
    const count = qs('[data-count]');
    const empty = qs('[data-empty]');
    let activeFilter = 'all';
    const render = () => {
      const query = (search?.value || '').trim().toLowerCase();
      let visible = 0;
      cards.forEach((card) => {
        const show = (activeFilter === 'all' || card.dataset.category === activeFilter)
          && (!query || card.dataset.name.toLowerCase().includes(query));
        card.hidden = !show;
        if (show) visible += 1;
      });
      if (count) count.textContent = String(visible);
      if (empty) empty.hidden = visible > 0;
      if (window.gsap) window.gsap.set(cards.filter((card) => !card.hidden), { opacity: 1, y: 0 });
      window.ScrollTrigger?.refresh();
    };
    filters.forEach((button) => on(button, 'click', () => {
      activeFilter = button.dataset.filter;
      filters.forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
      render();
    }));
    if (search) on(search, 'input', render);

    const copyButton = qs('[data-copy-code]');
    const status = qs('[data-copy-status]');
    if (copyButton && status) {
      on(copyButton, 'click', async () => {
        const code = copyButton.dataset.code || '';
        let copied = false;
        try {
          if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
          await navigator.clipboard.writeText(code);
          copied = true;
        } catch {
          const field = document.createElement('textarea');
          field.value = code;
          field.setAttribute('readonly', '');
          field.style.position = 'fixed';
          field.style.opacity = '0';
          document.body.appendChild(field);
          field.select();
          try { copied = document.execCommand('copy'); } catch { copied = false; }
          field.remove();
          copyButton.focus();
        }
        if (!copied) {
          status.textContent = 'Copy unavailable. Select HALLOWEEN and copy it manually.';
          return;
        }
        const original = status.textContent;
        copyButton.textContent = 'Copied';
        status.textContent = 'HALLOWEEN copied. $10 off eligible tools with a $100 minimum eligible-item subtotal, before shipping and tax. Starts Oct 18, 7:42 PM PDT / Oct 19, 02:42 UTC, 2026. Once per order; no discount stacking.';
        window.setTimeout(() => {
          copyButton.textContent = 'Copy code';
          status.textContent = original;
        }, 2600);
      });
    }

    const registerCleanup = () => initialized.set(root, () => cleanups.reverse().forEach((cleanup) => cleanup()));
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    if (!gsap || !ScrollTrigger) {
      registerCleanup();
      return;
    }

    gsap.registerPlugin(ScrollTrigger);
    const media = gsap.matchMedia();
    const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const reduce = () => reduceQuery.matches;
    const rebuildForMotionPreference = () => {
      const teardown = initialized.get(root);
      initialized.delete(root);
      teardown?.();
      init(root);
    };
    on(reduceQuery, 'change', rebuildForMotionPreference);

    const chapters = qsa('[data-story]');
    const viewer = qs('.icz-story-grid');
    const progressButtons = qsa('[data-story-phase]');
    let rebuildStory = null;

    const clearAnimations = (tweens, clearStory = true) => {
      tweens.forEach((tween) => {
        tween.scrollTrigger?.kill();
        tween.kill();
      });
      if (clearStory) chapters.forEach((chapter) => {
          const stage = chapter.querySelector('.icz-mobile-stage');
          const nodes = [...chapter.querySelector('.icz-chapter-content')?.children || []];
          gsap.set([stage, ...nodes], { clearProps: 'transform,opacity,visibility' });
        });
      if (!clearStory) cards.forEach((card) => gsap.set(card, { clearProps: 'transform,opacity,visibility' }));
    };

    const storyMotion = (chapter, tweens) => {
      const stage = chapter.querySelector('.icz-mobile-stage');
      const content = chapter.querySelector('.icz-chapter-content');
      if (stage) {
        const direction = chapter.dataset.story === 'ju' ? 6 : -6;
        tweens.push(gsap.fromTo(stage, { y: 56, scale: .94, rotationY: direction }, {
          y: 0, scale: 1, rotationY: 0, ease: 'none',
          scrollTrigger: { trigger: chapter, start: 'top 92%', end: 'top 60%', scrub: .6 }
        }));
      }
      if (content) {
        const nodes = [...content.children];
        tweens.push(gsap.fromTo(nodes, { y: 32, opacity: .35 }, {
          y: 0, opacity: 1, ease: 'none', stagger: .035,
          scrollTrigger: { trigger: chapter, start: 'top 92%', end: 'top 60%', scrub: .6 }
        }));
      }
    };

    let heroEntryPlayed = false;
    const playHeroEntry = () => {
      if (heroEntryPlayed || reduce()) return;
      const images = qsa('.icz-hero-product-wrap img');
      const copyChildren = qsa('.icz-hero-copy > *');
      if (!images.length) return;
      heroEntryPlayed = true;
      gsap.fromTo(images, { y: -42, scale: .96, opacity: 0 }, {
        y: 0, scale: 1, opacity: 1, duration: 1.6, stagger: .18, ease: 'power3.out', overwrite: true
      });
      if (copyChildren.length) {
        gsap.fromTo(copyChildren, { y: 28, opacity: 0 }, {
          y: 0, opacity: 1, duration: .8, stagger: .08, delay: .12, ease: 'power3.out', overwrite: true
        });
      }
    };

    const setupAdaptiveStory = () => {
      if (reduce() || !viewer || !chapters.length) return undefined;
      let activeCleanup = () => {};
      let resizeTimer = 0;
      let mode = 'none';
      let phaseDistance = 0;
      let pinTrigger = null;
      let timeline = null;
      let currentPhase = 0;

      const clearPinnedState = () => {
        if (timeline) timeline.kill();
        if (pinTrigger) pinTrigger.kill();
        timeline = null;
        pinTrigger = null;
        viewer.classList.remove('icz-story-pinned');
        viewer.style.removeProperty('--icz-story-viewer-height');
        chapters.forEach((chapter) => {
          chapter.removeAttribute('aria-hidden');
          chapter.inert = false;
          chapter.style.removeProperty('pointer-events');
          chapter.querySelector('h3')?.removeAttribute('tabindex');
        });
        chapters.forEach((chapter) => {
          const stage = chapter.querySelector('.icz-mobile-stage');
          const nodes = [...chapter.querySelector('.icz-chapter-content')?.children || []];
          gsap.set([chapter, stage, ...nodes], { clearProps: 'transform,opacity,visibility' });
        });
        progressButtons.forEach((button, index) => button.setAttribute('aria-pressed', String(index === 0)));
      };

      const measure = () => {
        if (viewer.classList.contains('icz-story-pinned')) clearPinnedState();
        const headerHeight = header?.getBoundingClientRect().height || 78;
        const viewerHeight = Math.max(0, window.innerHeight - headerHeight - 24);
        const bodyHeight = Math.max(0, viewerHeight - 64);
        const contentHeight = Math.max(...chapters.map((chapter) => chapter.querySelector('.icz-chapter-content')?.getBoundingClientRect().height || 0));
        const sceneHeight = Math.min(...chapters.map((chapter) => chapter.querySelector('.icz-mobile-stage')?.getBoundingClientRect().height || 0));
        return {
          bodyHeight,
          headerHeight,
          fits: window.innerWidth >= 1100
            && window.innerHeight >= 740
            && bodyHeight - 48 >= contentHeight
            && sceneHeight >= 360
        };
      };

      const buildCompact = () => {
        mode = 'compact';
        const tweens = [];
        chapters.forEach((chapter) => storyMotion(chapter, tweens));
        activeCleanup = () => clearAnimations(tweens);
      };

      const buildPinned = (bodyHeight, headerHeight, preservePhase) => {
        mode = 'pinned';
        phaseDistance = window.innerHeight * 1.5;
        viewer.style.setProperty('--icz-story-viewer-height', String(bodyHeight) + 'px');
        viewer.classList.add('icz-story-pinned');
        chapters.forEach((chapter) => chapter.querySelector('h3')?.setAttribute('tabindex', '-1'));

        chapters.slice(1).forEach((chapter, index) => {
          gsap.set(chapter, { autoAlpha: 0 });
          gsap.set(chapter.querySelector('.icz-mobile-stage'), {
            y: 56, scale: .94, rotationY: index === 0 ? 6 : -6
          });
          gsap.set([...chapter.querySelector('.icz-chapter-content')?.children || []], { y: 32, opacity: .25 });
        });
        gsap.set(chapters[0], { autoAlpha: 1 });
        gsap.set(chapters[0].querySelector('.icz-mobile-stage'), { y: 0, scale: 1, rotationY: 0 });
        gsap.set([...chapters[0].querySelector('.icz-chapter-content')?.children || []], { y: 0, opacity: 1 });

        const syncPhase = (nextIndex, transferFocus = true) => {
          const oldChapter = chapters[Number.isFinite(syncPhase.index) ? syncPhase.index : 0];
          const nextChapter = chapters[nextIndex];
          if (!nextChapter) return;
          const outgoingFocus = transferFocus && oldChapter !== nextChapter && oldChapter?.contains(document.activeElement);
          currentPhase = nextIndex;
          syncPhase.index = nextIndex;
          chapters.forEach((chapter, index) => {
            const active = index === nextIndex;
            chapter.setAttribute('aria-hidden', String(!active));
            chapter.inert = !active;
            chapter.style.pointerEvents = active ? 'auto' : 'none';
          });
          progressButtons.forEach((button, index) => button.setAttribute('aria-pressed', String(index === nextIndex)));
          if (outgoingFocus) progressButtons[nextIndex]?.focus({ preventScroll: true });
        };

        timeline = gsap.timeline({
          defaults: { ease: 'none' },
          onUpdate: () => {
            const time = timeline?.time() || 0;
            syncPhase(time < .925 ? 0 : time < 2.075 ? 1 : 2);
          },
          scrollTrigger: {
            trigger: viewer,
            start: 'top top+=' + headerHeight,
            end: () => '+=' + phaseDistance,
            pin: true,
            scrub: .8,
            anticipatePin: 1
          }
        });

        const addTransition = (oldIndex, nextIndex, start, direction) => {
          const oldChapter = chapters[oldIndex];
          const nextChapter = chapters[nextIndex];
          const stage = nextChapter.querySelector('.icz-mobile-stage');
          const nodes = [...nextChapter.querySelector('.icz-chapter-content')?.children || []];
          timeline.to(oldChapter, { autoAlpha: 0, duration: .45 }, start);
          timeline.to(nextChapter, { autoAlpha: 1, duration: .45 }, start);
          if (stage) timeline.to(stage, { y: 0, scale: 1, rotationY: 0, duration: .45 }, start);
          if (nodes.length) timeline.to(nodes, { y: 0, opacity: 1, duration: .22, stagger: { amount: .18 } }, start);
        };
        addTransition(0, 1, .70, 6);
        addTransition(1, 2, 1.85, -6);
        timeline.to({}, { duration: .70 }, 2.30);
        timeline.progress([.35, 1.5, 2.65][preservePhase] / 3, true);
        pinTrigger = timeline.scrollTrigger;

        const syncToPhase = (index) => {
          if (!pinTrigger) return;
          const target = pinTrigger.start + phaseDistance * ([.35, 1.5, 2.65][index] / 3);
          window.scrollTo({ top: target, behavior: reduce() ? 'auto' : 'smooth' });
        };
        const phaseHandlers = progressButtons.map((button, index) => {
          const handler = () => syncToPhase(index);
          button.addEventListener('click', handler);
          return () => button.removeEventListener('click', handler);
        });
        syncPhase(preservePhase, false);
        activeCleanup = () => {
          phaseHandlers.forEach((cleanup) => cleanup());
          clearPinnedState();
        };
      };

      const rebuild = () => {
        const preserved = currentPhase;
        activeCleanup();
        activeCleanup = () => {};
        const info = measure();
        if (info.fits) buildPinned(info.bodyHeight, info.headerHeight, preserved);
        else buildCompact();
        ScrollTrigger.refresh();
      };
      rebuildStory = rebuild;
      const onResize = () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(rebuild, 150);
      };
      window.addEventListener('resize', onResize);
      rebuild();
      return () => {
        window.removeEventListener('resize', onResize);
        window.clearTimeout(resizeTimer);
        if (rebuildStory === rebuild) rebuildStory = null;
        activeCleanup();
        activeCleanup = () => {};
        if (mode === 'pinned') clearPinnedState();
      };
    };

    const setupMobileStory = () => {
      if (reduce()) return undefined;
      const tweens = [];
      chapters.forEach((chapter) => storyMotion(chapter, tweens));
      return () => clearAnimations(tweens);
    };
    media.add('(min-width: 740px)', setupAdaptiveStory);
    media.add('(max-width: 739px)', setupMobileStory);

    const art = qs('.icz-hero-art');
    const copy = qs('.icz-hero-copy');
    const setupParallax = (artShift, copyShift, sharedShift = '') => {
      if (reduce()) return undefined;
      if (art && !sharedShift) gsap.to(art, { y: artShift, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: .6 } });
      if (copy) gsap.to(copy, { y: copyShift, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: .6 } });
      if (hero && sharedShift) gsap.to(hero, { '--icz-hero-shift': sharedShift, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: .6 } });
      playHeroEntry();
      return undefined;
    };
    media.add('(min-width: 960px)', () => setupParallax(60, 30, '60px'));
    media.add('(max-width: 959px)', () => setupParallax(32, 14));

    const animateCards = () => {
      if (reduce()) return undefined;
      const tweens = cards.map((card) => gsap.fromTo(card, { y: 24, opacity: .25 }, {
        y: 0, opacity: 1, duration: .5, ease: 'power2.out',
        scrollTrigger: { trigger: card, start: 'top 92%', end: 'top 72%', scrub: .6 }
      }));
      return () => clearAnimations(tweens, false);
    };
    media.add('(min-width: 740px)', animateCards);
    media.add('(max-width: 739px)', animateCards);

    const refresh = () => window.setTimeout(() => ScrollTrigger.refresh(), 60);
    window.addEventListener('load', refresh, { once: true });
    document.fonts?.ready?.then(() => {
      rebuildStory?.();
      refresh();
    });
    qsa('img').forEach((image) => {
      if (!image.complete) on(image, 'load', refresh);
    });
    cleanups.push(() => media.revert());
    cleanups.push(() => window.removeEventListener('load', refresh));
    registerCleanup();
  };

  const destroy = (scope = document) => {
    const root = getRoot(scope);
    if (!root) return;
    initialized.get(root)?.();
    initialized.delete(root);
  };

  init(document);
  document.addEventListener('shopify:section:load', () => { destroy(document); init(document); });
  document.addEventListener('shopify:section:unload', () => destroy(document));
})();

